import { CalendarIcon } from "@heroicons/react/16/solid";
import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { getAddressString } from "@/core/utils/address";
import { formatTime } from "@/core/utils/date";
import InfoPair from "./info-pair";
import { NameAndDate, NameAndDateSkeleton } from "./name-and-date";
import type { ReferralContentProps } from "./referralContent";

export const ScheduledReferralCardSkeleton = () => (
  <Stack fullWidth>
    <Stack
      direction="row"
      gap="sm"
      align="center"
      className="rounded-t-2xl bg-green-5 px-4 py-2"
      fullWidth
    >
      <Skeleton className="h-4 w-4" /> {/* Calendar icon */}
      <Skeleton className="h-4 w-24" /> {/* Date */}
    </Stack>
    <Stack className="px-4 pb-4" fullWidth>
      <NameAndDateSkeleton />
      <Stack gap="sm">
        <Skeleton className="h-4 w-32" /> {/* Services */}
        <Skeleton className="h-4 w-16" /> {/* Address label */}
        <Skeleton className="h-3 w-48" /> {/* Address */}
      </Stack>
    </Stack>
  </Stack>
);

export const ScheduledReferralCardContent = ({
  referral,
  onLost,
  onMoveToPaid,
  onDelete,
}: ReferralContentProps) => {
  const appointmentDate = referral.appointment_date
    ? formatTime(referral.appointment_date)
    : "No date";

  return (
    <Stack fullWidth>
      <Stack
        direction="row"
        gap="sm"
        align="center"
        className="rounded-t-2xl bg-green-5 px-4 py-2"
        fullWidth
      >
        <CalendarIcon className="h-4 w-4 text-tertiary-text" />
        <Typography variant="captionW1">{appointmentDate}</Typography>
      </Stack>
      <Stack className="px-4 pb-4" fullWidth>
        <NameAndDate
          referral={referral}
          hideDate
          onLost={onLost}
          onMoveToPaid={onMoveToPaid}
          onDelete={onDelete}
        />
        <Stack gap="sm">
          <InfoPair
            label="Services"
            info={referral.service_needed || "No services"}
          />
          <Typography variant="bodyW2">Address:</Typography>
          <Typography variant="captionW1" textColor="secondary">
            {getAddressString(referral.address) || "Address not known"}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};
